import {
    createContext,
    ReactNode,
    useContext,
    useState,
    useCallback,
    useRef
} from 'react';
import {
    AuthData,
    Authorization,
    AuthTokens,
    BaseAuthenticatedState,
    CustomerAuthenticatedState,
    NonCustomerAuthenticatedState
} from '../types';
import {decodeJwt} from '../lib/jwt';
import isLocalOrDevEnvironment from '../lib/isLocalOrDevEnvironment';
import unknownErrorToString from '../lib/unknownErrorToString';
import isProblemDetails from '../lib/isProblemDetails';
import {toast} from 'react-toastify';
import CustomClaimTypes from '../lib/CustomClaimTypes';
import refreshAuthentication from '../api/auth/refreshAuthentication';
import isAuthError from '../lib/isAuthError';
import {useNavigate} from 'react-router-dom';
import {RoutePaths} from '../RoutePaths';

const tenSecondsInMilliseconds = 10 * 1000;

function tokenHasExpired(access_token: string): boolean {
    const decodedToken = decodeJwt(access_token);
    const payload = decodedToken.payload;
    const expires = Number(payload.exp);
    // Seconds since Unix epoch
    const expiryDate = new Date(expires * 1000);
    const now = new Date();
    return now.getTime() >= expiryDate.getTime() - tenSecondsInMilliseconds;
}

function getAuthData(access_token: string | null | undefined): AuthData {
    if (!access_token || access_token == '') {
        return {
            isAuthenticated: false,
            roles: []
        };
    }

    const decodedToken = decodeJwt(access_token);
    const payload = decodedToken.payload;

    const knownKeys = [
        'unique_name',
        'email',
        'nameid',
        'aud',
        'iss',
        'exp',
        'iat',
        'nbf',
        'role',
        'given_name',
        'family_name',
        CustomClaimTypes.can_change_password,
        CustomClaimTypes.customer_id,
        CustomClaimTypes.person_id,
        CustomClaimTypes.has_multiple_customers
    ];

    const baseAuthData: BaseAuthenticatedState = {
        isAuthenticated: true,
        roles: [],
        id: payload.nameid,
        unique_name: payload.unique_name,
        email: payload.email,
        given_name: payload.given_name,
        family_name: payload.family_name,
        can_change_password:
            payload[CustomClaimTypes.can_change_password] == 'true',
        hasCustomerLogin: !!payload[CustomClaimTypes.customer_id]
    };

    const authData = {...baseAuthData} as Record<string, unknown>;

    Object.keys(payload).forEach(key => {
        if (!knownKeys.includes(key)) {
            authData[key] = payload[key];
        }
    });

    let roles = payload.role || [];
    if (typeof roles === 'string') {
        roles = [roles];
    }

    authData.roles = roles;

    if (payload[CustomClaimTypes.customer_id]) {
        return {
            ...authData,
            hasCustomerLogin: true,
            customer_id: payload[CustomClaimTypes.customer_id],
            person_id: payload[CustomClaimTypes.person_id],
            has_multiple_customers:
                payload[CustomClaimTypes.has_multiple_customers] == 'true'
        } as CustomerAuthenticatedState;
    } else {
        return {
            ...authData,
            hasCustomerLogin: false
        } as NonCustomerAuthenticatedState;
    }
}

function getItem(key: 'access_token' | 'refresh_token' | 'redir') {
    if (isLocalOrDevEnvironment) {
        return window.localStorage.getItem(key) ?? null;
    }

    return window.sessionStorage.getItem(key) ?? null;
}

function setItem(
    key: 'access_token' | 'refresh_token' | 'redir',
    value: string | null | undefined
) {
    if (isLocalOrDevEnvironment) {
        window.localStorage.setItem(key, value ?? '');
    } else {
        window.sessionStorage.setItem(key, value ?? '');
    }
}

function removeItem(key: 'access_token' | 'refresh_token' | 'redir') {
    if (isLocalOrDevEnvironment) {
        window.localStorage.removeItem(key);
    } else {
        window.sessionStorage.removeItem(key);
    }
}

export type AuthStateManager = {
    baseUri: string;
    access_token: string;
    refresh_token: string;
    authData: AuthData;

    loggedInUserId(): string | null;
    isAuthenticated: boolean;
    isInRole(role: string): boolean;
    isSysAdmin(): boolean;
    isAuthorizedForResource(resource: Authorization): boolean;
    logout(): void;
    setSignInRedirect(redirect: string): void;
    removeSignInRedirect(): string | null;
    getSignInRedirect(): string | null;
    redirectOnAuthenticated(redir?: string | null): void;
    getAccessToken(): Promise<string>;
    onAuthenticated(authResponse: AuthTokens): void;
    setAuthorizationHeader(headers: Headers): Promise<void>;

    hasCustomerLogin: boolean;
    hasMultipleCustomers: boolean;
    customerId: string | null;
    personId: string | null;
    givenName: string | null;
    familyName: string | null;
    email: string | null;
    roles: string[];
};

const emptyAuthStateManager: AuthStateManager = {
    baseUri: '',
    access_token: '',
    refresh_token: '',
    authData: {
        isAuthenticated: false,
        roles: []
    },
    loggedInUserId: () => null,
    isAuthenticated: false,
    isInRole: (role: string) => (console.log(role), false),
    isSysAdmin: () => false,
    isAuthorizedForResource: (resource: Authorization) => (
        console.log(resource),
        false
    ),
    logout: () => {},
    setSignInRedirect: (redirect: string) => {
        console.log(redirect);
    },
    removeSignInRedirect: () => null,
    getSignInRedirect: () => null,
    redirectOnAuthenticated: (redir?: string | null) => {
        console.log(redir);
    },
    getAccessToken: async () => '',
    onAuthenticated: (authResponse: AuthTokens) => {
        console.log(authResponse);
    },
    setAuthorizationHeader: async (headers: Headers) => {
        console.log(headers);
    },
    hasCustomerLogin: false,
    hasMultipleCustomers: false,
    customerId: null,
    personId: null,
    givenName: null,
    familyName: null,
    email: null,
    roles: []
};

const AuthStateManagerContext = createContext<AuthStateManager>(
    emptyAuthStateManager
);

const AuthStateManagerProvider = ({
    baseUri,
    children
}: {
    baseUri: string;
    children: ReactNode;
}) => {
    const authStateManager = useAuthStateManagerImpl(baseUri);

    return (
        <AuthStateManagerContext.Provider value={authStateManager}>
            {children}
        </AuthStateManagerContext.Provider>
    );
};

function useAuthStateManagerImpl(baseUri: string): AuthStateManager {
    const navigate = useNavigate();

    const redirectOnAuthenticated = (redir?: string | null) => {
        // First priority: use the passed redir parameter
        if (redir) {
            navigate(redir);
            return;
        }

        // Second priority: check stored redirect
        const storedRedirect = removeSignInRedirect();

        if (storedRedirect) {
            navigate(storedRedirect);
            return;
        }

        // Default: navigate to dashboard
        navigate(RoutePaths.dashboard());
    };

    const [access_token, set_access_token] = useState(
        () => getItem('access_token') ?? ''
    );

    const [refresh_token, set_refresh_token] = useState(
        () => getItem('refresh_token') ?? ''
    );

    const [authData, setAuthData] = useState(getAuthData(access_token));

    const refreshingPromiseRef = useRef<Promise<string> | null>(null);

    const loggedInUserId = (): string | null => {
        return authData.isAuthenticated ? (authData.id ?? null) : null;
    };

    const isAuthenticated = authData.isAuthenticated;

    const isInRole = (role: string) => authData.roles.includes(role);

    const isSysAdmin = () => authData.roles.includes('sysadmin');

    const isAuthorizedForResource = (resource: Authorization): boolean => {
        if (!resource.authorize) {
            return true;
        }

        if (!isAuthenticated) {
            return false;
        }

        /*
        if (isSysAdmin()) {
            return true;
        }
        */

        if (resource.roles && resource.roles.length > 0) {
            return resource.roles.some(r => isInRole(r));
        }

        return true;
    };

    const logout = useCallback(() => {
        set_access_token('');
        set_refresh_token('');

        removeItem('access_token');
        removeItem('refresh_token');

        setAuthData({
            isAuthenticated: false,
            roles: []
        });
    }, []);

    const setSignInRedirect = (redirect: string) => {
        setItem('redir', redirect);
    };

    const removeSignInRedirect = () => {
        const redirect = getItem('redir');
        removeItem('redir');
        return redirect;
    };

    const getSignInRedirect = () => {
        return getItem('redir');
    };

    const onAuthenticated = useCallback((authResponse: AuthTokens) => {
        set_access_token(authResponse.access_token);
        set_refresh_token(authResponse.refresh_token);

        setItem('access_token', authResponse.access_token);
        setItem('refresh_token', authResponse.refresh_token);

        setAuthData(getAuthData(authResponse.access_token));

        return authResponse.access_token;
    }, []);

    const refreshTokenRequest = useCallback(async () => {
        const authResponse = await refreshAuthentication(refresh_token ?? '');

        if (
            isAuthError(authResponse) ||
            authResponse instanceof Error ||
            isProblemDetails(authResponse)
        ) {
            const errorMessage = isProblemDetails(authResponse)
                ? unknownErrorToString(authResponse)
                : 'Your login session has expired, please login again.';

            toast.error(errorMessage);
            logout();
            return '';
        } else {
            if (!authResponse.access_token) {
                toast.error(
                    'Session refresh failed: No access token received. Please log in again.'
                );
                logout();
                return '';
            }

            const newAccessToken = onAuthenticated(authResponse);
            return newAccessToken;
        }
    }, [refresh_token, onAuthenticated, logout]);

    const getAccessToken = useCallback(async () => {
        if (access_token) {
            if (tokenHasExpired(access_token)) {
                if (refreshingPromiseRef.current) {
                    try {
                        return await refreshingPromiseRef.current;
                    } catch {
                        logout();
                    }
                } else {
                    const refreshPromise = refreshTokenRequest();
                    refreshingPromiseRef.current = refreshPromise;

                    try {
                        return await refreshPromise;
                    } catch {
                        // Error is already handled in refreshTokenRequest
                    } finally {
                        refreshingPromiseRef.current = null;
                    }
                }
            } else {
                return access_token;
            }
        }

        return '';
    }, [access_token, refreshTokenRequest, logout]);

    const setAuthorizationHeader = useCallback(
        async (headers: Headers) => {
            const access_token = await getAccessToken();
            if (access_token && access_token !== '') {
                headers.set('Authorization', `bearer ${access_token}`);
            }
        },
        [getAccessToken]
    );

    const hasCustomerLogin =
        isAuthenticated && 'hasCustomerLogin' in authData
            ? authData.hasCustomerLogin
            : false;
    const hasMultipleCustomers =
        isAuthenticated && 'has_multiple_customers' in authData
            ? (authData as CustomerAuthenticatedState).has_multiple_customers
            : false;
    const customerId =
        isAuthenticated && hasCustomerLogin
            ? (authData as CustomerAuthenticatedState).customer_id
            : null;
    const personId =
        isAuthenticated && hasCustomerLogin
            ? (authData as CustomerAuthenticatedState).person_id
            : null;
    const givenName = isAuthenticated ? authData.given_name : null;
    const familyName = isAuthenticated ? authData.family_name : null;
    const email = isAuthenticated ? authData.email : null;
    const roles = authData.roles;

    return {
        baseUri,
        access_token,
        refresh_token,
        authData,
        loggedInUserId,
        isAuthenticated,
        isInRole,
        isSysAdmin,
        isAuthorizedForResource,
        logout,
        setSignInRedirect,
        removeSignInRedirect,
        getSignInRedirect,
        redirectOnAuthenticated,
        getAccessToken,
        onAuthenticated,
        setAuthorizationHeader,
        hasCustomerLogin,
        hasMultipleCustomers,
        customerId,
        personId,
        givenName,
        familyName,
        email,
        roles
    };
}

const useAuthStateManager = () => useContext(AuthStateManagerContext);

// eslint-disable-next-line react-refresh/only-export-components
export {useAuthStateManager, AuthStateManagerProvider};
